import React, { useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Divider,
  Chip,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Print as PrintIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  MoneyOff as MoneyOffIcon,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface CreditNoteData {
  numero_avoir: string;
  numero_facture_originale: string;
  date_paiement_original: string;
  date_annulation: string;
  motif_annulation: string;
  first_name: string;
  last_name: string;
  registration_number: string;
  parent_name?: string;
  classe: string;
  montant_avoir: number;
  school_year: string;
  payment_method?: string;
  cancelled_by_name?: string;
  fne_avoir_reference?: string;
  fne_avoir_token?: string;
}

interface CreditNoteDialogProps {
  open: boolean;
  onClose: () => void;
  creditNoteData: CreditNoteData | null;
}

const formatAmount = (amount: number) =>
  new Intl.NumberFormat('fr-FR').format(amount);

const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const CreditNoteDialog: React.FC<CreditNoteDialogProps> = ({
  open,
  onClose,
  creditNoteData,
}) => {
  const creditNoteRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = React.useState(false);

  const handleDownloadPDF = async () => {
    if (!creditNoteRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(creditNoteRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#fff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageHeight = 295;
      const pageWidth = 210;
      
      let imgWidth = pageWidth;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Si l'image est plus haute qu'une page A4, on la redimensionne proportionnellement
      if (imgHeight > pageHeight) {
        imgHeight = pageHeight - 10; // 10mm de marge de sécurité
        imgWidth = (canvas.width * imgHeight) / canvas.height;
      }

      // Centrer horizontalement si réduit
      const marginX = (pageWidth - imgWidth) / 2;

      pdf.addImage(imgData, 'PNG', marginX, 5, imgWidth, imgHeight); // 5mm de marge haut
      pdf.save(`facture_avoir_${creditNoteData?.numero_avoir || 'AV'}.pdf`);
    } catch (err) {
      console.error('Erreur lors du téléchargement de la facture d\'avoir:', err);
      alert('Erreur lors du téléchargement. Veuillez réessayer.');
    } finally {
      setDownloading(false);
    }
  };

  const handlePrint = () => {
    if (!creditNoteRef.current) return;
    const content = creditNoteRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Facture d'Avoir - ${creditNoteData?.numero_avoir}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 0; padding: 10px; color: #333; }
              table { width: 100%; border-collapse: collapse; margin: 10px 0; }
              td { padding: 4px 8px; border: 1px solid #ddd; font-size: 13px; }
              .header { text-align: center; }
              @media print { 
                @page { size: A4 portrait; margin: 5mm; }
                body { margin: 0; padding: 0; } 
                /* Forcer l'ajustement de taille pour l'impression physique */
                html, body { width: 100%; height: 100%; }
              }
            </style>
          </head>
          <body>${content}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); printWindow.close(); }, 300);
    }
  };

  if (!creditNoteData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      {/* Header rouge distinctif */}
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #c62828 0%, #e53935 100%)',
          color: 'white',
          p: 0,
        }}
      >
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <MoneyOffIcon sx={{ fontSize: 36 }} />
            <Box>
              <Typography variant="h5" fontWeight={800} letterSpacing={1}>
                FACTURE D'AVOIR
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                Document de crédit — Annulation de paiement
              </Typography>
            </Box>
          </Stack>
          <Button onClick={onClose} sx={{ color: 'white', minWidth: 0 }}>
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Zone imprimable / exportable PDF */}
        <Box ref={creditNoteRef} sx={{ p: 2, bgcolor: 'white', color: 'black', maxWidth: '100%', margin: '0 auto' }}>
          {/* Logo + en-tête école */}
          <Box sx={{ textAlign: 'center', mb: 1.5 }}>
            <img
              src="/img/pages/434000183.jpg"
              alt="Logo École"
              style={{ height: 60, maxWidth: '100%', objectFit: 'contain', marginBottom: 4 }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <Typography variant="h5" fontWeight={800} sx={{ color: 'black' }}>
              LA MAISON DES ENFANTS — LA PETITE ACADÉMIE
            </Typography>
            <Typography variant="body2" sx={{ color: 'black' }}>
              Abidjan, Côte d'Ivoire
            </Typography>
          </Box>

          <Divider sx={{ mb: 1.5, borderColor: '#000' }} />

          {/* Titre AVOIR */}
          <Box
            sx={{
              textAlign: 'center',
              mb: 1.5,
              py: 1,
              border: '2px solid #000',
              borderRadius: 1,
              bgcolor: '#f5f5f5',
            }}
          >
            <Typography variant="h5" fontWeight={900} letterSpacing={2} sx={{ color: 'black' }}>
              FACTURE D'AVOIR
            </Typography>
            <Chip
              label="DOCUMENT DE CRÉDIT"
              variant="outlined"
              size="small"
              sx={{ mt: 0.5, fontWeight: 700, letterSpacing: 1, borderColor: '#000', color: 'black' }}
            />
          </Box>

          {/* Références */}
          <Table size="small" sx={{ mb: 1.5, '& td': { py: 0.5 } }}>
            <TableBody>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, color: 'black', width: '50%', borderColor: '#ccc' }}>
                  N° Avoir
                </TableCell>
                <TableCell sx={{ fontWeight: 800, color: 'black', fontSize: '1.1rem', borderColor: '#ccc' }}>
                  {creditNoteData.numero_avoir}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'black', borderColor: '#ccc' }}>Facture originale annulée</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'black', borderColor: '#ccc' }}>{creditNoteData.numero_facture_originale}</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: 'black', borderColor: '#ccc' }}>Date du paiement original</TableCell>
                <TableCell sx={{ color: 'black', borderColor: '#ccc' }}>{formatDate(creditNoteData.date_paiement_original)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'black', borderColor: '#ccc' }}>Date d'annulation</TableCell>
                <TableCell sx={{ color: 'black', fontWeight: 700, borderColor: '#ccc' }}>
                  {formatDate(creditNoteData.date_annulation)}
                </TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600, color: 'black', borderColor: '#ccc' }}>Année scolaire</TableCell>
                <TableCell sx={{ color: 'black', borderColor: '#ccc' }}>{creditNoteData.school_year}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* Informations élève */}
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: 'black', textTransform: 'uppercase', fontSize: '0.8rem' }}>
            Informations de l'élève
          </Typography>
          <Table size="small" sx={{ mb: 1.5, border: '1px solid #ccc', borderRadius: 1, '& td': { py: 0.5 } }}>
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '50%', bgcolor: '#f5f5f5', color: 'black', borderColor: '#ccc' }}>Nom & Prénom</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'black', borderColor: '#ccc' }}>
                  {creditNoteData.last_name} {creditNoteData.first_name}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: 'black', borderColor: '#ccc' }}>Matricule</TableCell>
                <TableCell sx={{ color: 'black', borderColor: '#ccc' }}>{creditNoteData.registration_number || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: 'black', borderColor: '#ccc' }}>Classe</TableCell>
                <TableCell sx={{ color: 'black', borderColor: '#ccc' }}>{creditNoteData.classe || '-'}</TableCell>
              </TableRow>
              {creditNoteData.parent_name && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: 'black', borderColor: '#ccc' }}>Parent / Tuteur</TableCell>
                  <TableCell sx={{ color: 'black', borderColor: '#ccc' }}>{creditNoteData.parent_name}</TableCell>
                </TableRow>
              )}
              {creditNoteData.payment_method && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', color: 'black', borderColor: '#ccc' }}>Mode de paiement initial</TableCell>
                  <TableCell sx={{ color: 'black', borderColor: '#ccc' }}>{creditNoteData.payment_method}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Motif d'annulation */}
          <Box
            sx={{
              mb: 1.5,
              p: 1,
              border: '1px solid #ccc',
              borderRadius: 1,
              bgcolor: '#f5f5f5',
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.25, color: 'black', fontSize: '0.8rem' }}>
              Motif d'annulation
            </Typography>
            <Typography variant="body2" sx={{ color: 'black', fontSize: '0.8rem' }}>
              {creditNoteData.motif_annulation}
            </Typography>
          </Box>

          {/* Montant de l'avoir */}
          <Box
            sx={{
              textAlign: 'center',
              mb: 1.5,
              py: 1,
              px: 2,
              bgcolor: '#f5f5f5',
              borderRadius: 1,
              border: '2px solid #000',
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, color: 'black' }}>
              MONTANT CRÉDITÉ (AVOIR)
            </Typography>
            <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: 1, color: 'black' }}>
              - {formatAmount(Number(creditNoteData.montant_avoir))} FCFA
            </Typography>
            <Typography variant="caption" sx={{ mt: 0.5, color: 'black' }}>
              Ce montant est crédité au bénéfice du parent / tuteur
            </Typography>
          </Box>

          {/* === CERTIFICATION FNE === */}
          {creditNoteData.fne_avoir_reference && (
            <Box
              sx={{
                mb: 1.5,
                p: 1,
                border: '1px solid #000',
                borderRadius: 1,
                bgcolor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.25, color: 'black', fontSize: '0.8rem' }}>
                  ✅ FACTURE D'AVOIR CERTIFIÉE — DGI PLATFORME FNE
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5, color: 'black' }}>
                  Référence FNE :
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight={800}
                  sx={{ fontFamily: 'monospace', fontSize: '1rem', letterSpacing: 1, color: 'black' }}
                >
                  {creditNoteData.fne_avoir_reference}
                </Typography>
              </Box>
              {creditNoteData.fne_avoir_token && (
                <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(creditNoteData.fne_avoir_token)}`}
                    alt="QR Code FNE Avoir"
                    style={{ width: 80, height: 80, display: 'block' }}
                  />
                  <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'black', fontSize: '0.65rem' }}>
                    Scanner pour vérifier
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Annulé par */}
          {creditNoteData.cancelled_by_name && (
            <Typography variant="body2" sx={{ textAlign: 'center', mb: 1, color: 'black', fontSize: '0.8rem' }}>
              Annulation effectuée par : <strong>{creditNoteData.cancelled_by_name}</strong>
            </Typography>
          )}

          <Divider sx={{ mb: 1, borderColor: '#000' }} />

          {/* Pied de page */}
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'black', fontSize: '0.7rem' }}>
            Ce document est une facture d'avoir officielle émise par La Petite Académie. Conservez-le précieusement.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #eee', gap: 1 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Imprimer
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadPDF}
          disabled={downloading}
          sx={{ fontWeight: 700 }}
        >
          {downloading ? 'Téléchargement...' : 'Télécharger PDF'}
        </Button>
        <Button variant="outlined" onClick={onClose}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditNoteDialog;
